import { useEffect, useState } from "react";
import {
  lerCursosDoAluno,
  lerTiposDePublicacao,
  publicarDocumento,
  type CursoDoAluno,
  type TipoDeDocumento,
} from "../lib/faculdade";
import { buscarAluno, type AlunoEncontrado } from "../lib/admin";
import { Aviso, CAMPO } from "./ui";

// Admin › Faculdade › Publicar — a secretaria põe um documento no acervo do
// aluno, sem pedido (000282).
//
// Hoje o único tipo é o INFORME DE RENDIMENTOS, e a tela não o crava: ela lista
// os tipos de fluxo `publicacao` do catálogo. Cravar o nome aqui faria o segundo
// tipo nascer invisível.
//
// ── Por que uma tela em vez de um botão na fila ────────────────────────────
//
// O balcão (Admin › Faculdade › Documentos) é a fila do que está PENDENTE, e
// publicar não tem pendência: ninguém pediu. Pôr o ato lá misturaria "atender o
// que o aluno pediu" com "entregar o que a casa decidiu entregar", e o operador
// que procura um não quer ver o outro.
//
// ── O gesto, e por que ele é por ALUNO ─────────────────────────────────────
//
// O informe é por (aluno, curso, ano). A tela começa pela busca porque é assim
// que a pergunta chega à secretaria — "o informe da fulana" —, e o curso sai da
// LISTA DELA (`sec_cursos_do_aluno`), nunca do catálogo inteiro: com 12 cursos
// e nenhuma relação com o aluno, errar é questão de tempo, e o documento errado
// fica no acervo dele para sempre.
export default function AdminPublicarDocumento() {
  const [tipos, setTipos] = useState<TipoDeDocumento[]>([]);
  const [erroInicial, setErroInicial] = useState(false);

  const [termo, setTermo] = useState("");
  const [buscando, setBuscando] = useState(false);
  const [achados, setAchados] = useState<AlunoEncontrado[] | null>(null);
  // A recusa do banco vem PRONTA (`buscar_aluno` levanta 22023 com uma frase
  // escrita para gente). Traduzir aqui criaria uma segunda versão da regra.
  const [avisoDaBusca, setAvisoDaBusca] = useState<string | null>(null);

  const [aluno, setAluno] = useState<AlunoEncontrado | null>(null);
  const [cursos, setCursos] = useState<CursoDoAluno[] | null>(null);

  const [tipoId, setTipoId] = useState("");
  const [curso, setCurso] = useState("");
  const [ano, setAno] = useState(String(new Date().getFullYear() - 1));
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [resposta, setResposta] = useState<{ ok: boolean; texto: string } | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const t = await lerTiposDePublicacao();
        setTipos(t);
        if (t.length === 1) setTipoId(t[0].id);
      } catch {
        setErroInicial(true);
      }
    })();
  }, []);

  async function procurar(e: React.FormEvent) {
    e.preventDefault();
    if (termo.trim().length < 3) return;
    setBuscando(true);
    setResposta(null);
    setAvisoDaBusca(null);
    const r = await buscarAluno(termo.trim());
    if (r.fase === "ok") setAchados(r.itens);
    else {
      setAchados(null);
      setAvisoDaBusca(
        r.fase === "recusado" ? r.mensagem : "Não deu para procurar agora. Tente de novo.",
      );
    }
    setBuscando(false);
  }

  async function escolher(a: AlunoEncontrado) {
    setAluno(a);
    setAchados(null);
    setCursos(null);
    setCurso("");
    setResposta(null);
    try {
      const c = await lerCursosDoAluno(a.profile_id);
      setCursos(c);
      if (c.length === 1) setCurso(c[0].curso_slug);
    } catch {
      setCursos([]);
    }
  }

  async function publicar(e: React.FormEvent) {
    e.preventDefault();
    if (!aluno || !arquivo || !tipoId || !curso) return;
    setEnviando(true);
    setResposta(null);
    const r = await publicarDocumento({
      userId: aluno.profile_id,
      tipoId,
      cursoSlug: curso,
      ano: ano.trim() === "" ? null : Number(ano),
      arquivo,
    });
    setEnviando(false);
    setResposta({ ok: r.ok, texto: r.ok ? (r.detalhe ?? "publicado") : (r.erro ?? "não deu") });
    if (r.ok) {
      // O arquivo sai, o resto FICA: publicar o informe de vários anos do mesmo
      // aluno é o caso comum, e limpar tudo obrigaria a refazer a busca.
      setArquivo(null);
      const campo = document.getElementById("arquivo-da-publicacao") as HTMLInputElement | null;
      if (campo) campo.value = "";
    }
  }

  if (erroInicial) {
    return (
      <Aviso titulo="Não deu para carregar os tipos">
        Recarregue a página; se continuar, fale com quem cuida do banco.
      </Aviso>
    );
  }

  if (tipos.length === 0) {
    return (
      <Aviso titulo="Nenhum documento de publicação no catálogo">
        Publicação é o fluxo em que a casa entrega sem o aluno pedir — hoje o informe de
        rendimentos. Se ele sumiu da lista, foi desativado em Admin › Catálogo.
      </Aviso>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <section className="surface p-5">
        <h2 className="font-display text-lg font-bold text-titulo">Publicar um documento</h2>
        <p className="mt-1 text-sm text-cinza">
          O documento entra direto no acervo do aluno, já entregue — ele não precisa pedir. Fica
          disponível para download mesmo depois que o curso termina.
        </p>

        <form onSubmit={procurar} className="mt-4 flex flex-wrap items-end gap-2">
          <label className="min-w-64 flex-1">
            <span className="mb-1 block text-sm font-medium text-tinta">
              Quem vai receber
            </span>
            <input
              value={termo}
              onChange={(e) => setTermo(e.target.value)}
              placeholder="nome, e-mail ou CPF"
              data-busca-aluno
              className={CAMPO}
            />
          </label>
          <button type="submit" disabled={buscando || termo.trim().length < 3} className="btn-contorno">
            {buscando ? "Procurando…" : "Procurar"}
          </button>
        </form>

        {avisoDaBusca && <p className="mt-3 text-sm text-erro-tinta">{avisoDaBusca}</p>}
        {achados !== null && achados.length === 0 && (
          <p className="mt-3 text-sm text-cinza">Ninguém com esse termo.</p>
        )}
        {achados !== null && achados.length > 0 && (
          <ul className="mt-3 divide-y divide-borda">
            {achados.slice(0, 12).map((a) => (
              <li key={a.profile_id} data-aluno={a.profile_id} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <p className="truncate font-medium text-tinta">{a.nome ?? "(sem nome)"}</p>
                  <p className="truncate text-sm text-cinza">{a.email}</p>
                </div>
                <button type="button" onClick={() => void escolher(a)} className="btn-contorno text-sm">
                  Escolher
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {aluno && (
        <section className="surface p-5">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="font-display text-base font-bold text-titulo">
              {aluno.nome ?? aluno.email}
            </h3>
            <button
              type="button"
              onClick={() => { setAluno(null); setCursos(null); setResposta(null); }}
              className="text-sm font-medium text-link underline-offset-4 hover:underline"
            >
              trocar de aluno
            </button>
          </div>

          {cursos === null && <p className="mt-3 text-sm text-cinza">Carregando os cursos…</p>}

          {cursos !== null && cursos.length === 0 && (
            // Sem curso não há onde publicar, e a tela DIZ em vez de mostrar um
            // formulário que vai ser recusado pelo banco.
            <p className="mt-3 text-sm text-cinza">
              Esta pessoa não tem acesso nem matrícula em curso nenhum. Confira se é a conta certa —
              o informe é por curso.
            </p>
          )}

          {cursos !== null && cursos.length > 0 && (
            <form onSubmit={publicar} className="mt-4 grid gap-4 sm:grid-cols-2">
              <label>
                <span className="mb-1 block text-sm font-medium text-tinta">Documento</span>
                <select value={tipoId} onChange={(e) => setTipoId(e.target.value)} className={CAMPO} data-publicar-tipo>
                  <option value="">escolha…</option>
                  {tipos.map((t) => (
                    <option key={t.id} value={t.id}>{t.nome}</option>
                  ))}
                </select>
              </label>

              <label>
                <span className="mb-1 block text-sm font-medium text-tinta">Curso</span>
                <select value={curso} onChange={(e) => setCurso(e.target.value)} className={CAMPO} data-publicar-curso>
                  <option value="">escolha…</option>
                  {cursos.map((c) => (
                    <option key={c.curso_slug} value={c.curso_slug}>{c.curso_nome}</option>
                  ))}
                </select>
              </label>

              <label>
                <span className="mb-1 block text-sm font-medium text-tinta">Ano de referência</span>
                <input
                  value={ano}
                  onChange={(e) => setAno(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  inputMode="numeric"
                  data-publicar-ano
                  className={CAMPO}
                />
                {/* O padrão é o ano PASSADO porque o informe de rendimentos é
                    emitido no começo do ano seguinte ao dos pagamentos — é o
                    valor certo em quase toda publicação, e ele é editável. */}
                <span className="mt-1 block text-xs text-cinza">
                  O ano a que o documento se refere, não o de hoje.
                </span>
              </label>

              <label>
                <span className="mb-1 block text-sm font-medium text-tinta">Arquivo</span>
                <input
                  id="arquivo-da-publicacao"
                  data-publicar-arquivo
                  type="file"
                  onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
                  className={CAMPO}
                />
              </label>

              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={enviando || !arquivo || !tipoId || !curso}
                  className="pill-cta disabled:opacity-60"
                  data-publicar-enviar
                >
                  {enviando ? "Publicando…" : "Publicar no acervo do aluno"}
                </button>
              </div>
            </form>
          )}

          {resposta && (
            // A resposta do BANCO, com a frase dele. Traduzir aqui criaria uma
            // segunda tabela de frases, e a que divergisse em silêncio seria a
            // que ninguém lê.
            <p
              data-publicar-resposta={resposta.ok ? "ok" : "erro"}
              className={`mt-4 rounded-lg px-3 py-2 text-sm ${
                resposta.ok
                  ? "bg-aviso-fundo text-aviso-tinta"
                  : "bg-erro-fundo text-erro-tinta"
              }`}
            >
              {resposta.texto}
            </p>
          )}
        </section>
      )}
    </div>
  );
}
